import Group from 'components/utils/Group'
import Image from 'components/Common/Image/Image'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { dateDifference } from 'lib/datetime/dateUtils'
import useOrderItemsEligibleForCarHireBundledDiscount from 'hooks/CarHire/useOrderItemsEligibleForCarHireBundledDiscount'
import { pluralizeToString } from 'lib/string/pluralize'
import Heading from 'LuxKit/Typography/Heading'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import LayoutContainer from 'components/Common/LayoutContainer'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import CarHireDiscountUpcomingOrderCarousel from './CarHireDiscountUpcomingOrderCarousel'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { connect } from 'react-redux'

const MainContainer = styled(Group)`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr auto;
    column-gap: ${rem(24)};
    row-gap: ${rem(32)};
  }
`

const HotelContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr ${rem(68)};
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(164)} 1fr;
    gap: ${rem(24)};
  }
`

const HotelImageWrapper = styled.div`
  grid-column-start: 2;
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-column-start: 1;
  }
`

const HotelContentWrapper = styled(Group)`
  grid-column-start: 1;
  grid-row: 1;
  
  ${mediaQueryUp.tablet} {
    grid-column-start: 2;
  }
`

const DaysToGoLabel = styled(StructureLabel)`
  text-transform: uppercase;
`

const ViewAllButtonWrapper = styled(Group)`
  align-self: end;
  padding: ${rem(10)} ${rem(12)};

  ${mediaQueryUp.tablet} {
    padding: 0;
    grid-column-start: 2;
    grid-row: 1;
  }
`

interface Props {
  order: App.Order;
}

interface MappedProps {
  isFetchingCarousel?: boolean;
}

function CarHireDiscountUpcomingOrder(props: Props & MappedProps) {
  const { order, isFetchingCarousel } = props

  const { earliestItem, latestItem, searchLink } = useOrderItemsEligibleForCarHireBundledDiscount(order.id)

  const daysToGo = useMemo(() => {
    if (!earliestItem) return 0
    return dateDifference(new Date(earliestItem.startDate), new Date()).days
  }, [earliestItem])

  const dispatch = useAppDispatch()

  const onSearch = useCallback(() => {
    dispatch(pushWithRegion(searchLink))
  }, [searchLink, dispatch])

  const onManageBooking = useCallback(() => {
    dispatch(pushWithRegion(`/account/my-escapes/order/${order.id}`))
  }, [dispatch, order.id])

  return <>
    <LayoutContainer>
      <MainContainer direction="vertical" gap={20} desktopGap={32}>
        {isFetchingCarousel && <HotelContainer>
          <HotelImageWrapper>
            <CSSBreakpoint min="desktop">
              <LoadingBox style={{ width: 164, height: 88 }} />
            </CSSBreakpoint>
            <CSSBreakpoint max="tablet">
              <LoadingBox style={{ width: 68, height: 68 }} />
            </CSSBreakpoint>
          </HotelImageWrapper>
          <HotelContentWrapper direction="vertical" gap={4} desktopGap={8}>
            <LoadingBox style={{ width: 100, height: 24 }} />
            <LoadingBox style={{ width: 200, height: 28 }} />
            <LoadingBox style={{ width: 100, height: 20 }} />
          </HotelContentWrapper>
        </HotelContainer>}
        {!isFetchingCarousel && !!earliestItem && !!latestItem && <HotelContainer>
          <HotelImageWrapper>
            <CSSBreakpoint min="desktop">
              <Image id={earliestItem.offer.image.id} width={164} height={88} />
            </CSSBreakpoint>
            <CSSBreakpoint max="tablet">
              <Image id={earliestItem.offer.image.id} width={68} height={68} />
            </CSSBreakpoint>
          </HotelImageWrapper>
          <HotelContentWrapper direction="vertical" gap={4} desktopGap={8}>
            {daysToGo > 0 && <LabelGroup>
              <DaysToGoLabel variant="promotion">
                {pluralizeToString('day', daysToGo)} to go
              </DaysToGoLabel>
            </LabelGroup>}
            <Heading variant="heading3">
              {earliestItem.offer.name}
            </Heading>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <LineCalendarIcon size="S" />
              <BodyText variant="medium">
                <FormatDateTime value={earliestItem.startDate} format="day-month-name" />{' - '}
                <FormatDateTime value={latestItem.endDate} format="day-month-name" />
              </BodyText>
              <BodyText variant="medium">·</BodyText>
              <TextLink size="medium" onClick={onManageBooking}>
                Manage booking
              </TextLink>
            </Group>
          </HotelContentWrapper>
        </HotelContainer>}
        {!isFetchingCarousel && <CSSBreakpoint min="desktop">
          <ViewAllButtonWrapper direction="horizontal" horizontalAlign="end" verticalAlign="baseline">
            <TextButton kind="tertiary" size="medium" onClick={onSearch}>
              View all options
            </TextButton>
          </ViewAllButtonWrapper>
        </CSSBreakpoint>}
      </MainContainer>
    </LayoutContainer>
    <CarHireDiscountUpcomingOrderCarousel order={order} />
    {!isFetchingCarousel && <CSSBreakpoint max="tablet">
      <ViewAllButtonWrapper direction="horizontal" horizontalAlign="end" verticalAlign="baseline">
        <TextButton kind="tertiary" size="medium" onClick={onSearch}>
          View all options
        </TextButton>
      </ViewAllButtonWrapper>
    </CSSBreakpoint>}
  </>
}

function mapStateToProps(state: App.State, ownProps: Props): MappedProps {
  const listKey = Object.keys(state.carHire.offerLists).find((key) => key.includes(ownProps.order.id))

  return {
    isFetchingCarousel: listKey ? state.carHire.offerLists[listKey].fetching : false,
  }
}

export default connect(mapStateToProps)(CarHireDiscountUpcomingOrder)
